import React, { useContext, useEffect, useState } from "react";
import { Modal } from "reactstrap";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { faLongArrowAltRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getProductTier, getTierSportCount, SubscriptionContext } from "../../../services/SubscriptionService";
import { UPDATE_USER_SPORT, UPDATE_USER_SUBSCRIPTION } from "../../../gql/mutations";
import { TierItem } from "../../../typings/pricing";
import GAService from "../../../services/GAService";
import AnalyticsService from "../../../services/AnalyticsService";
import styles from "./styles.scss";
import SportSelector from "src/components/SportSelector";
import { useMutation } from "@apollo/client";
import Spinner from "src/components/Spinner";
import { wait } from "src/services/UtilService";

const CancelModal = ({
  visible,
  toggleModal,
  userSubscription,
  currentTier,
}: {
  visible: boolean;
  toggleModal(): void;
  // optional because they need to load
  userSubscription?: any;
  currentTier?: TierItem;
}) => {
  const [updateSubscription, subResult] = useMutation(UPDATE_USER_SUBSCRIPTION);
  const [updateSports, sportResult] = useMutation(UPDATE_USER_SPORT);
  const ctx = useContext(SubscriptionContext);

  const canUpdate = !ctx.subscriptionSportsUpdateTime?.isAfter();
  const allSportAccess = getTierSportCount(getProductTier(currentTier?.key ?? null)) === 0; // prettier-ignore

  const subbedSportNames: string[] = userSubscription?.sports?.map((s: any) => s.sport) ?? []; // prettier-ignore
  const [sportList, _setSportList] = useState(subbedSportNames);

  // Only update if able
  const setSportList = (items: string[]) => {
    if (canUpdate && !allSportAccess) {
      _setSportList(items);
    }
  };

  // User subs don't come in right away so reset on open
  // In case sports changed but no submit, reset on close
  useEffect(() => {
    setSportList(subbedSportNames);
  }, [visible]);

  // Since it's null when loading let's return to avoid errors
  if (!currentTier) {
    return null;
  }

  const onCancel = async () => {
    if (subResult.loading || sportResult.loading) {
      return;
    }

    const trackingProps = {
      screen: "CancelModal",
      page: "Pricing",
      tab: currentTier ? currentTier.durationTitle : undefined,
      subscriptionLevel: currentTier ? `${currentTier.title} ${currentTier.durationTitle}` : undefined,
      sports: ctx.subscriptionSports.length ? ctx.subscriptionSports.toString() : "All",
      upgradeSource: ctx.subscriptionUpgradeSource,
    };

    try {
      await updateSubscription({
        variables: {
          currentUserSubscriptionId: userSubscription.id,
          cancelAtPeriodEnd: true,
        },
        errorPolicy: "all",
        awaitRefetchQueries: true,
        refetchQueries: () => ["userSubscriptionQuery"],
      });

      toggleModal();

      AnalyticsService.track("Cancel Subscription", {
        ...trackingProps,
        success: true,
      });

      GAService.sendEvent({
        hitType: "event",
        eventCategory: "Cancel",
        eventAction: window.location.href,
        eventLabel: currentTier?.key,
      });
    } catch (e: any) {
      console.log("Error cancelling subscription: ", e.message);

      AnalyticsService.track("Cancel Subscription", {
        ...trackingProps,
        success: false,
      });
    }
  };

  const onSubmitSports = async () => {
    if (subResult.loading || sportResult.loading) {
      return;
    }

    if (!canUpdate || allSportAccess) {
      return toggleModal();
    }

    try {
      const sportsRes = await updateSports({
        variables: { sports: sportList },
      });
      const { data, errors } = sportsRes;
      if (errors || !data?.updateUserSport) {
        throw new Error("Failed to change sports.");
      }
      const { token } = data.updateUserSport;
      toggleModal();
      await wait(300); // hide modal before sub refresh
      await ctx.setSubscriptionLevel(token);
    } catch (e) {
      console.error("[updateSports] error: ", e);
    }
  };

  const onClose = () => {
    if (subResult.loading || sportResult.loading) {
      return;
    }

    toggleModal();
  };

  return (
    <Modal wrapClassName="rotoql-cancel-modal" isOpen={visible} centered>
      <div className="modal-close" onClick={onClose}>
        <Icon path={mdiClose} size="22px" color="#262626" />
      </div>
      <div className="modal-heading">Did you know you could change your sport?</div>
      <div className="modal-subheading">You can switch sports once per week</div>
      {Boolean(subResult.error) && <div className="modal-error-text">Error cancelling your subscription.</div>}
      {Boolean(sportResult.error) && <div className="modal-error-text">Error updating your sports.</div>}
      {!canUpdate && (
        <div className="modal-error-text">
          You can switch sports on {ctx.subscriptionSportsUpdateTime?.format("M/D/YYYY h:mma")}.
        </div>
      )}
      {allSportAccess && <div className="modal-error-text">Your subscription gives you access to all sports.</div>}
      <div style={{ height: 15 }} />
      <SportSelector
        tier={getProductTier(currentTier.key)}
        sportList={sportList}
        setSportList={setSportList}
        showSuggestions={true}
        titleStyle={{ textTransform: "uppercase", color: "#909CAC" }}
      />
      <div className="modal-button-row">
        <div className="modal-submit-button" onClick={onSubmitSports}>
          {sportResult.loading ? (
            <Spinner size={20} color="#fff" />
          ) : (
            <>
              Submit New Sports
              <FontAwesomeIcon icon={faLongArrowAltRight} style={{ marginLeft: 8 }} />
            </>
          )}
        </div>
        <div className="modal-cancel-button" onClick={onCancel}>
          {subResult.loading ? <Spinner size={20} color="#004198" /> : <>No, I Want To Cancel</>}
        </div>
      </div>
      <style jsx>{styles}</style>
    </Modal>
  );
};

export default CancelModal;
